import { action, computed, makeObservable, observable } from "mobx";


export class Entity<EntityInterface> {
    protected primaryKey: string;
    localValue: EntityInterface | null = null;
    remoteValue: EntityInterface | null = null;
    remoteFutureValue: EntityInterface | null = null;

    constructor(
        primaryKey: string,
        localValue: EntityInterface | null,
        remoteValue: EntityInterface | null,
        isFrozen = false
    ) {
        this.primaryKey = primaryKey;
        this.localValue = localValue
        if (isFrozen) {
            this.remoteFutureValue = remoteValue;
        } else {
            this.remoteValue = remoteValue;
        }
        makeObservable(this, {
            localValue: observable.deep,
            remoteValue: observable.deep,
            remoteFutureValue: observable.deep,
            value: computed,
            isDeleted: computed,
            isPending: computed,
            isFuture: computed,
            isChanged: computed,
            revert: action,
            update: action,
            updateRemote: action,
            delete: action
        });
    }

    get id() {
        return (this.value as any)[this.primaryKey];
    }

    get value(): EntityInterface {
        return {...(this.localValue || this.remoteValue)} as EntityInterface;
    }

    get isDeleted(): boolean {
        return !!(this.value as any).is_deleted;
    }

    copy(): this {
        return new (this.constructor as any)(this.primaryKey, this.localValue, this.remoteValue) as this;
    }

    get isChanged(): boolean {
        return this.localValue != null;
    }

    get isPending(): boolean {
        return this.remoteFutureValue != null;
    }

    get isFuture(): boolean {
        return this.localValue == null && this.remoteValue == null && this.remoteFutureValue != null;
    }

    get isOrphan(): boolean {
        return this.localValue == null && this.remoteValue == null && this.remoteFutureValue == null;
    }

    revert() {
        this.localValue = null;
        if (this.isPending) {
            if (this.remoteValue != null) {
                this.remoteValue = this._merge(this.remoteValue, this.remoteFutureValue);
            } else {
                this.remoteValue = this.remoteFutureValue;
            }
            this.remoteFutureValue = null;
        }
    }

    update(value: Partial<EntityInterface>) {
        if (!this.localValue) {
            this.localValue = this.remoteValue;
        }
        this.localValue = {...this.localValue, ...value} as EntityInterface;
    }

    updateRemote(value: Partial<EntityInterface>, isFrozen = false) {
        if (isFrozen) {
            if (this.remoteFutureValue) {
                this.remoteFutureValue = this._merge(this.remoteFutureValue, {...this.remoteFutureValue, ...value});
            } else {
                this.remoteFutureValue = {...(this.remoteValue || {}), ...(value as EntityInterface)};
            }
        } else {
            if (this.remoteValue) {
                this.remoteValue = this._merge(this.remoteValue, {...this.remoteValue, ...value})
            } else {
                this.remoteValue = value as EntityInterface;
            }
        }
    }

    delete() {
        (this as any).update({is_deleted: true});
    }

    protected _merge(oldEntity: any, newEntity: any) {
        if (newEntity.updated_at > oldEntity.updated_at) {
            return newEntity;
        } else {
            return oldEntity;
        }
    }
}
